import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import { Button } from '@spotahome/ui-library';
import { formatDisplayDate } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import getGqlClient from '../../graphql/client';
import { getOptimizedDates } from '../../graphql/queries/optimizeDates';

import './OptimizeDates.scss';

const fetchOptimizedDates = async (listingId, moveIn, moveOut) => {
  const res = await getOptimizedDates(getGqlClient())({
    listingId,
    moveIn,
    moveOut
  });

  return res;
};

const formatSavingsText = (savings, contractType) =>
  savings.moneySaving
    ? trans(`book_now_card.optimize_dates.money_savings.${contractType}`, {
        amount:
          contractType === 'monthly'
            ? savings.moneySaving
            : savings.moneySaving * 2
      })
    : trans('book_now_card.optimize_dates.time_savings');

const formatContractType = contractType =>
  `<span class="optimize-dates__contract-type">${trans(
    `contract_type.label.type-${contractType}`
  )}</span>`;

const formatDate = date =>
  `<span class="optimize-dates__date">${formatDisplayDate(date)}</span>`;

const formatDateChoices = ({ moveIn, moveOut }) => {
  const moveInText = moveIn
    ? trans('book_now_card.optimize_dates.from', {
        date: formatDate(moveIn)
      })
    : '';
  const moveOutText = moveOut
    ? trans('book_now_card.optimize_dates.to', {
        date: formatDate(moveOut)
      })
    : '';

  return [moveInText, moveOutText].filter(Boolean).join(' ');
};

const OptimizeDates = ({
  contractType,
  moveIn,
  moveOut,
  onApplyOptimizedDates,
  listingId
}) => {
  const [dismissed, setDismissed] = useState(false);

  const handleDismissed = () => {
    setDismissed(true);
  };

  useEffect(() => {
    setDismissed(false);
  }, [moveIn, moveOut]);

  const [optimizedDates, setOptimizedDates] = useState();

  useEffect(() => {
    let active = true;

    async function load() {
      const res = await fetchOptimizedDates(listingId, moveIn, moveOut);
      if (!active) {
        return;
      }
      setOptimizedDates(res);
    }

    load();
    return () => {
      active = false;
    };
  }, [moveIn, moveOut]);

  if (!optimizedDates || dismissed) {
    return null;
  }

  const handleApplyOptimizedDates = () => {
    onApplyOptimizedDates({
      moveIn: optimizedDates.moveIn || moveIn,
      moveOut: optimizedDates.moveOut || moveOut
    });
  };

  return (
    <div className="optimize-dates">
      <span
        dangerouslySetInnerHTML={{
          __html: `${trans('book_now_card.optimize_dates.contract_type', {
            contractType: formatContractType(contractType)
          })}
          ${formatSavingsText(optimizedDates.savings, contractType)}`
        }}
      />
      <span
        className="optimize-dates__proposal"
        dangerouslySetInnerHTML={{
          __html: formatDateChoices(optimizedDates)
        }}
      />
      <div className="optimize-dates__buttons">
        <Button
          renderContent={() => trans('book_now_card.optimize_dates.dismiss')}
          color="link"
          onClick={handleDismissed}
        />
        <Button
          renderContent={() => trans('book_now_card.optimize_dates.apply')}
          color="light"
          onClick={handleApplyOptimizedDates}
        />
      </div>
    </div>
  );
};

OptimizeDates.propTypes = {
  contractType: PropTypes.string.isRequired,
  moveIn: PropTypes.string.isRequired,
  moveOut: PropTypes.string.isRequired,
  listingId: PropTypes.string.isRequired,
  onApplyOptimizedDates: PropTypes.func.isRequired
};

export default OptimizeDates;
